import type {
  DispatchAssignedDriverRead,
  DispatchTripPassengerRead,
  Passenger,
  PassengerRead,
  PatchedTripPassengerRead,
  RunBambiRunTripPassengerRead,
} from '../external/bambi-api/bambiApi';
import type { TripListPassengerRead } from '../external/bambi-api/tripsApi';

enum EntityType {
  ATTENDANT = 'attendant',
  DRIVER = 'driver',
  PASSENGER = 'passenger',
}

const ENTITY_TYPE_TO_LABEL = {
  [EntityType.ATTENDANT]: 'Attendant',
  [EntityType.DRIVER]: 'Driver',
  [EntityType.PASSENGER]: 'Passenger',
};

export interface INamedEntity {
  first_name?: string;
  last_name?: string;
  full_name?: string;
  deleted_at?: string | null;
}

type GetDeletedAtFn<T extends INamedEntity> = (entity: T) => string | null;

/**
 * @param getDeletedAt - The caller can pass a function that returns deleted_at according to its relationships and own logic.
 * @returns The formatted name of the entity.
 */
export function formatFullName<T extends INamedEntity>(
  entity: T,
  getDeletedAt?: GetDeletedAtFn<T>,
  entityType?: EntityType
): string {
  const formattedName =
    entity.full_name ||
    `${entity.first_name || ''} ${entity.last_name || ''}`.trim();

  // If defined, this function overrides and ignores the presence of deleted_at
  if (getDeletedAt) {
    const deletedAt = getDeletedAt(entity);
    const label = entityType ? ENTITY_TYPE_TO_LABEL[entityType] : '';
    return deletedAt
      ? label
        ? `${formattedName} [${label} deleted]`
        : `${formattedName} [deleted]`
      : formattedName;
  }

  if (entity.deleted_at) {
    const label = entityType ? ENTITY_TYPE_TO_LABEL[entityType] : '';

    return label
      ? `${formattedName} [${label} deleted]`
      : `${formattedName} [deleted]`;
  }
  return formattedName;
}

export const getDispatchAssignedDriverDeletedAt: GetDeletedAtFn<
  DispatchAssignedDriverRead
> = (assignedDriver) => {
  return (
    assignedDriver.deleted_at ||
    assignedDriver.driver_deleted_at ||
    assignedDriver.membership_deleted_at
  );
};

export const formatFullNameIgnoreDeletedAt = (entity: INamedEntity) => {
  return formatFullName(entity, () => null);
};

export const formatPassengerFullName = (
  passenger:
    | Passenger
    | PassengerRead
    | DispatchTripPassengerRead
    | RunBambiRunTripPassengerRead
    | PatchedTripPassengerRead
    | TripListPassengerRead
) => {
  return formatFullName(passenger, undefined, EntityType.PASSENGER);
};
